import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { orderAPI, paymentAPI } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Scissors,
  CreditCard,
  ShoppingBag,
  Lock
} from 'lucide-react';

export default function CheckoutPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { store } = useStore();
  const { user, isAuthenticated } = useAuth();
  const { items, getTotal, clearCart } = useCart();
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [loading, setLoading] = useState(false);

  const subtotal = getTotal();
  const tax = subtotal * 0.22;
  const total = subtotal + tax;

  const handleCheckout = async () => {
    if (items.length === 0) {
      toast.error('Il carrello è vuoto');
      return;
    }

    setLoading(true);
    try {
      // Create order
      const orderData = {
        items: items.map(({ product, quantity }) => ({
          product_id: product.id,
          quantity
        }))
      };
      
      const orderRes = await orderAPI.create(orderData);
      const order = orderRes.data;
      
      // Create payment session
      const originUrl = window.location.origin;
      
      if (paymentMethod === 'stripe') {
        const paymentRes = await paymentAPI.createStripeSession(order.id, originUrl);
        
        if (paymentRes.data.url) {
          // Redirect to Stripe checkout
          window.location.href = paymentRes.data.url;
        } else {
          throw new Error('URL di pagamento non ricevuto');
        }
      } else {
        // PayPal
        const paymentRes = await paymentAPI.createPayPalOrder(order.id);
        toast.info(paymentRes.data.message || 'PayPal richiede configurazione');
      }
    } catch (error) {
      console.error('Errore checkout:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il checkout');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <ShoppingBag className="w-16 h-16 text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">Carrello vuoto</h1>
        <p className="text-muted-foreground mb-6">
          Aggiungi prodotti al carrello per procedere al checkout.
        </p>
        <Button asChild>
          <Link to={`/s/${slug}/shop`}>Vai allo Shop</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50">
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to={`/s/${slug}/carrello`} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Scissors className="w-6 h-6 text-primary" />
              <span className="font-semibold">{store?.name} - Checkout</span>
            </div>
          </div>
        </div>
      </header>

      <div className="pt-24 pb-12 section-padding">
        <div className="container-main max-w-4xl">
          <h1 className="text-3xl font-bold mb-8">Checkout</h1>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Checkout Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* User Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Informazioni Cliente</CardTitle>
                </CardHeader>
                <CardContent>
                  {isAuthenticated ? (
                    <div className="space-y-2">
                      <p><strong>Nome:</strong> {user?.name}</p>
                      <p><strong>Email:</strong> {user?.email}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-muted-foreground">
                        Per completare l'ordine è necessario effettuare il login.
                      </p>
                      <div className="flex gap-2">
                        <Button asChild>
                          <Link to="/login">Accedi</Link>
                        </Button>
                        <Button variant="outline" asChild>
                          <Link to="/register">Registrati</Link>
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Payment Method */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Metodo di Pagamento</CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                    <div className="flex items-center space-x-3 p-4 border rounded-lg hover:border-primary/50 cursor-pointer">
                      <RadioGroupItem value="stripe" id="stripe" data-testid="payment-stripe" />
                      <Label htmlFor="stripe" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <CreditCard className="w-5 h-5" />
                          <div>
                            <p className="font-medium">Carta di Credito / Debito</p>
                            <p className="text-sm text-muted-foreground">Paga con Visa, Mastercard, etc.</p>
                          </div>
                        </div>
                      </Label>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-4 border rounded-lg hover:border-primary/50 cursor-pointer mt-3">
                      <RadioGroupItem value="paypal" id="paypal" data-testid="payment-paypal" />
                      <Label htmlFor="paypal" className="flex-1 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <div className="w-5 h-5 flex items-center justify-center font-bold text-blue-600">P</div>
                          <div>
                            <p className="font-medium">PayPal</p>
                            <p className="text-sm text-muted-foreground">Paga con il tuo account PayPal</p>
                          </div>
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>
                </CardContent>
              </Card>
            </div>
            
            {/* Order Summary */}
            <div>
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle>Riepilogo Ordine</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {items.map(({ product, quantity }) => (
                    <div key={product.id} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {product.name} x{quantity}
                      </span>
                      <span>{formatPrice(product.price * quantity)}</span>
                    </div>
                  ))}
                  
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotale</span>
                      <span>{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">IVA (22%)</span>
                      <span>{formatPrice(tax)}</span>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4 flex justify-between">
                    <span className="font-semibold">Totale</span>
                    <span className="font-bold text-lg text-primary">{formatPrice(total)}</span>
                  </div>
                  
                  <Button 
                    className="w-full mt-4" 
                    size="lg"
                    onClick={handleCheckout}
                    disabled={loading || !isAuthenticated}
                    data-testid="pay-btn"
                  >
                    {loading ? (
                      'Elaborazione...'
                    ) : (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        Paga {formatPrice(total)}
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-muted-foreground text-center">
                    I tuoi dati sono protetti da crittografia SSL
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
